<script setup>
import { ref, onMounted } from 'vue';
import { crmmAdminApi } from 'dashboard/helper/crmmAdminPanel';

const GROUPS = [
  ['Equipe', ['waitlist', 'funil', 'kb', 'spy', 'ltv', 'campanha']],
  ['Copiloto — IA sob demanda', ['draft', 'criar', 'consultar']],
  ['IAs por conversa — automáticas', ['triagem', 'sentimento', 'resumo', 'transcricao']],
  ['Rotinas automáticas', ['sla', 'reativacao']],
  ['Agenda', ['agenda']],
];
const MODELS = ['gpt-4o-mini', 'gpt-4o', 'gpt-4.1-mini', 'gpt-4.1'];
const TIPS = {
  waitlist: 'Lista de Espera: fila de contatos (nome, procedimento, janela), reordenar, agendar.',
  funil: 'Funil: board kanban de leads por estágio, tarefas e relatórios de conversão.',
  kb: 'Base de Conhecimento: textos/FAQ que a IA usa pra responder (busca semântica).',
  spy: 'Modo Spy (admin): abre conversas sem marcar como lida, com trilha de auditoria.',
  ltv: 'Painel LTV: histórico de valor do contato na lateral da conversa.',
  campanha: 'Campanhas: página de disparo em massa (template + filtro de público + throttle).',
  draft: 'Rascunho de IA: gera resposta sugerida na caixa; o atendente revisa.',
  criar: 'Criar agendamento (IA): cria agendamento direto da conversa.',
  consultar: 'Consultar horários (IA): consulta disponibilidade direto do chat.',
  triagem: 'Triagem: conversa nova ganha etiqueta de assunto e entra no funil.',
  sentimento: 'Sentimento: mensagem do cliente irritada ganha etiqueta "atencao".',
  resumo: 'Resumo ao resolver: nota privada com resumo + dados do contato.',
  transcricao: 'Transcrição: áudio do cliente vira texto numa nota privada (Whisper).',
  sla: 'SLA: conversa esperando resposta 15+ min ganha etiqueta "sla" (a cada 10 min).',
  reativacao: 'Reativação: conversa marcada "reativar" recebe 1 follow-up de IA (a cada 30 min).',
  agenda: 'Agenda (página): agendamentos do dia, criar/editar/remarcar, espelho no Google.',
};

const tenants = ref([]);
const ia = ref([]);
const msg = ref(''); const msgKind = ref('');
const newName = ref(''); const newAcct = ref(''); const newMsg = ref(''); const newKind = ref('');
const creating = ref(false);

const setMsg = (t, k) => { msg.value = t; msgKind.value = k || ''; };

const load = async () => {
  setMsg('Carregando...', '');
  try {
    const j = await crmmAdminApi('admin-features-list', {});
    tenants.value = Array.isArray(j) ? j : ((j && j.tenants) || []);
    ia.value = Array.isArray(j) ? [] : ((j && j.ia) || []);
    setMsg('', '');
  } catch (e) { setMsg('Falha ao listar: ' + ((e && e.message) || e), 'err'); }
};

const toggle = async (t, feature, ev) => {
  const value = ev.target.checked;
  setMsg('salvando ' + feature + '...', '');
  try {
    const j = await crmmAdminApi('admin-features-set', { tenant_id: t.tenant_id, feature, value });
    const row = Array.isArray(j) ? j[0] : j;
    if (!row || !row.tenant_id) throw new Error((row && row.error) || 'resposta inesperada');
    t.features = row.features || t.features;
    setMsg((row.display_name || t.tenant_id) + ' · ' + feature + ' = ' + value + ' ✓', 'ok');
  } catch (e) {
    ev.target.checked = !value;
    setMsg('FALHOU ' + feature + ': ' + ((e && e.message) || e), 'err');
  }
};

const setStatus = async (t, status) => {
  if (!window.confirm((status === 'suspended' ? 'Suspender' : 'Reativar') + ' ' + (t.display_name || t.tenant_id) + '?')) return;
  setMsg('mudando status...', '');
  try {
    const j = await crmmAdminApi('admin-tenants-set', { action: 'status', tenant_id: t.tenant_id, status });
    const row = Array.isArray(j) ? j[0] : j;
    if (!row || !row.tenant_id) throw new Error('resposta inesperada');
    setMsg((row.display_name || t.tenant_id) + ' agora está ' + row.status + ' ✓', 'ok');
    await load();
  } catch (e) { setMsg('FALHOU status: ' + ((e && e.message) || e), 'err'); }
};

const criar = async () => {
  const nome = (newName.value || '').trim();
  const acct = Number((newAcct.value || '').toString().trim());
  if (nome.length < 2) { newMsg.value = 'nome muito curto'; newKind.value = 'err'; return; }
  if (!Number.isInteger(acct) || acct < 1) { newMsg.value = 'número da conta inválido'; newKind.value = 'err'; return; }
  creating.value = true; newMsg.value = 'criando...'; newKind.value = '';
  try {
    const j = await crmmAdminApi('admin-tenants-set', { action: 'create', display_name: nome, chatwoot_account_id: acct });
    const row = Array.isArray(j) ? j[0] : j;
    if (!row || !row.tenant_id) throw new Error('resposta inesperada');
    if (row.criado === false) { newMsg.value = 'conta ' + acct + ' já existia (' + (row.display_name || row.tenant_id) + ')'; newKind.value = 'warn'; }
    else { newMsg.value = (row.display_name || nome) + ' criada ✓ — features desligadas; ligue no card.'; newKind.value = 'ok'; }
    newName.value = ''; newAcct.value = '';
    await load();
  } catch (e) { newMsg.value = 'FALHOU: ' + ((e && e.message) || e); newKind.value = 'err'; } finally { creating.value = false; }
};

const setIaModel = async (r, ev) => {
  const model = ev.target.value;
  try {
    const j = await crmmAdminApi('admin-features-ia-set', { ia: r.ia, model });
    const row = Array.isArray(j) ? j[0] : j;
    if (!row || !row.ia) throw new Error('resposta inesperada');
    r.model = row.model;
    setMsg(row.ia + ' agora usa ' + row.model + ' ✓', 'ok');
  } catch (e) { setMsg('FALHOU modelo: ' + ((e && e.message) || e), 'err'); }
};
const setIaLimit = async (r, ev) => {
  try {
    const j = await crmmAdminApi('admin-features-ia-set', { ia: r.ia, limite_dia: (ev.target.value || '').trim() });
    const row = Array.isArray(j) ? j[0] : j;
    if (!row || !row.ia) throw new Error('resposta inesperada');
    r.limite_dia = row.limite_dia;
    setMsg(row.ia + ': ' + (row.limite_dia != null ? ('alarme em ' + row.limite_dia + ' tokens/dia ✓') : 'alarme desligado ✓'), 'ok');
  } catch (e) { setMsg('FALHOU limite: ' + ((e && e.message) || e), 'err'); }
};

const fmt = (n) => Number(n || 0).toLocaleString('pt-BR');
const inGroup = (feats, f) => !!(feats && feats[f] === true);

onMounted(load);
</script>

<template>
  <div class="crmm-admin">
    <div class="wrap">
      <div class="hd">
        <h1>CRMM IA · Painel Super-Admin</h1>
        <button class="ghost" @click="load">Recarregar</button>
      </div>
      <div class="sub">Contas, features e IAs por conta (tenant). Efeito em até ~60s (cache do gate).</div>
      <div v-if="msg" class="pmsg" :class="msgKind">{{ msg }}</div>

      <div v-for="t in tenants" :key="t.tenant_id" class="tcard">
        <div class="thead">
          <span class="tname">{{ t.display_name || t.tenant_id }}<span class="conta">{{ t.chatwoot_account_id != null ? ('conta ' + t.chatwoot_account_id) : 'sem conta' }}</span></span>
          <span class="badge" :class="t.status === 'active' ? 'active' : (t.status === 'suspended' ? 'suspended' : 'outro')">{{ t.status }}</span>
          <span class="esp"></span>
          <button v-if="t.status === 'active'" class="ghost" @click="setStatus(t, 'suspended')">Suspender</button>
          <button v-else-if="t.status === 'suspended'" class="ghost" @click="setStatus(t, 'active')">Reativar</button>
        </div>
        <div class="groups">
          <div v-for="g in GROUPS" :key="g[0]" class="group">
            <h3>{{ g[0] }}</h3>
            <div class="pills">
              <span v-for="f in g[1]" :key="f" class="pill" :title="TIPS[f]">
                <span class="plabel">{{ f }}</span>
                <label class="tg">
                  <input type="checkbox" :checked="inGroup(t.features, f)" @change="(ev) => toggle(t, f, ev)" />
                  <span class="sl"></span>
                </label>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div v-if="!tenants.length" class="tcard">Nenhuma conta no registry ainda — crie a primeira abaixo.</div>

      <div class="row">
        <div class="card">
          <h2>Nova conta (tenant)</h2>
          <div class="novorow">
            <div style="flex:2 1 220px">
              <label class="lbl">Nome (como aparece aqui)</label>
              <input v-model="newName" class="txt" maxlength="64" placeholder="ex.: Clínica Sorriso" />
            </div>
            <div style="flex:1 1 140px">
              <label class="lbl">Nº da conta Chatwoot</label>
              <input v-model="newAcct" class="txt" inputmode="numeric" placeholder="ex.: 3" />
            </div>
            <button class="acao" :disabled="creating" @click="criar">Criar conta</button>
          </div>
          <div v-if="newMsg" class="pmsg" :class="newKind">{{ newMsg }}</div>
          <div class="hint">Cria no registry com todas as features desligadas — ligue uma a uma no card da conta. O nº da conta está na URL (app/accounts/<b>N</b>). Depois, adicione o bot <b>CRMM IA</b> como administrador da conta.</div>
        </div>
        <div class="card">
          <h2>IAs internas — modelo &amp; gasto de tokens</h2>
          <table>
            <thead><tr><th style="text-align:left">IA</th><th>Modelo (LLM)</th><th>Tokens hoje</th><th>Últimos 7 dias</th><th>Limite/dia 🔔</th></tr></thead>
            <tbody>
              <tr v-for="r in ia" :key="r.ia">
                <td class="nome">{{ r.ia }}</td>
                <td><select class="txt sel" :value="r.model" @change="(ev) => setIaModel(r, ev)"><option v-for="m in MODELS" :key="m" :value="m">{{ m }}</option></select></td>
                <td>{{ fmt(r.hoje) }}</td>
                <td>{{ fmt(r.sete_dias) }}</td>
                <td><input type="number" min="0" step="1000" class="txt lim" :value="r.limite_dia != null ? r.limite_dia : ''" placeholder="sem alarme" @change="(ev) => setIaLimit(r, ev)" /></td>
              </tr>
              <tr v-if="!ia.length"><td colspan="5" class="muted">sem dados de IA.</td></tr>
            </tbody>
          </table>
          <div class="hint">🤖 As IAs leem o modelo daqui a cada execução — vale já na próxima mensagem. 🔔 <b>Limite/dia</b>: passou, alerta no ntfy (1x/dia). Vazio = sem alarme.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.crmm-admin {
  --azul: #2ebdff; --ok: #5fe0a0; --warn: #ffd166; --bg: #001224; --card: #07203c;
  --linha: #173a5c; --txt: #e8f1fb; --muted: #8aa6c2; --inp: #0a2a4a; --red: #ff6b6b;
  background: var(--bg); color: var(--txt); height: 100%; overflow-y: auto;
  font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
}
.wrap { padding: 22px 28px 80px; }
.hd { display: flex; align-items: center; gap: 14px; }
h1 { color: var(--azul); margin: 4px 0 2px; font-size: 20px; flex: 1; }
.sub { color: var(--muted); font-size: 12px; margin-bottom: 16px; }
.card { background: var(--card); border: 1px solid var(--linha); border-radius: 12px; padding: 16px; margin-bottom: 16px; }
.card h2 { font-size: 14px; color: var(--azul); margin: 0 0 12px; }
.row { display: flex; gap: 16px; flex-wrap: wrap; align-items: stretch; margin-top: 16px; }
.row > .card { flex: 1 1 460px; margin-bottom: 0; }
.tcard { background: var(--card); border: 1px solid var(--linha); border-radius: 12px; padding: 16px 18px; margin-bottom: 16px; }
.thead { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 6px; }
.tname { font-size: 16px; font-weight: 800; }
.tname .conta { color: var(--muted); font-weight: 400; font-size: 12px; margin-left: 6px; }
.thead .esp { flex: 1; }
.badge { font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 10px; text-transform: uppercase; }
.badge.active { background: #0e3a2a; color: var(--ok); }
.badge.suspended { background: #3a3210; color: var(--warn); }
.badge.outro { background: #173a5c; color: var(--muted); }
.groups { display: flex; flex-wrap: wrap; gap: 10px 22px; margin-top: 8px; }
.group { flex: 1 1 230px; min-width: 220px; }
.group h3 { font-size: 11px; color: var(--azul); text-transform: uppercase; letter-spacing: .05em; margin: 8px 0 8px; font-weight: 700; }
.pills { display: flex; flex-wrap: wrap; gap: 8px; }
.pill { display: inline-flex; align-items: center; gap: 8px; padding: 6px 10px; background: var(--inp); border: 1px solid var(--linha); border-radius: 10px; font-size: 12.5px; cursor: help; }
.pill .plabel { font-weight: 600; }
.tg { position: relative; display: inline-block; width: 34px; height: 19px; vertical-align: middle; }
.tg input { opacity: 0; width: 0; height: 0; }
.tg .sl { position: absolute; cursor: pointer; inset: 0; background: #0a2a4a; border: 1px solid var(--linha); border-radius: 19px; transition: .15s; }
.tg .sl:before { content: ""; position: absolute; height: 13px; width: 13px; left: 2px; top: 2px; background: var(--muted); border-radius: 50%; transition: .15s; }
.tg input:checked + .sl { background: #0e3a2a; border-color: var(--ok); }
.tg input:checked + .sl:before { transform: translateX(15px); background: var(--ok); }
table { border-collapse: collapse; width: 100%; font-size: 13px; }
th, td { padding: 8px 10px; border-bottom: 1px solid var(--linha); text-align: center; }
th { color: var(--azul); font-size: 11px; text-transform: uppercase; letter-spacing: .04em; }
td.nome { text-align: left; font-weight: 700; }
td.muted { color: var(--muted); }
.pmsg { font-size: 12px; margin: 8px 0; min-height: 16px; }
.pmsg.err { color: var(--red); }
.pmsg.ok { color: var(--ok); }
.pmsg.warn { color: var(--warn); }
.hint { font-size: 11px; color: var(--muted); margin-top: 10px; line-height: 1.5; }
.novorow { display: flex; gap: 10px; flex-wrap: wrap; align-items: flex-end; }
.lbl { display: block; font-size: 11px; font-weight: 700; color: var(--azul); text-transform: uppercase; letter-spacing: .04em; margin: 0 0 5px; }
.txt { padding: 9px; background: var(--inp); color: var(--txt); border: 1px solid var(--linha); border-radius: 8px; font-size: 14px; width: 100%; }
.sel { padding: 6px; width: auto; }
.lim { width: 110px; padding: 6px; }
button.ghost { cursor: pointer; background: #07203c; color: var(--muted); border: 1px solid var(--linha); padding: 7px 12px; border-radius: 8px; font-size: 12px; }
button.ghost:hover { color: var(--txt); border-color: var(--azul); }
button.acao { cursor: pointer; background: #0e3a2a; color: var(--ok); border: 1px solid var(--ok); padding: 9px 16px; border-radius: 8px; font-size: 13px; font-weight: 700; }
button:disabled { opacity: .5; cursor: wait; }
</style>
